import { Box, Button, Typography, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import { httpService, usePortalApplicationContext, VerticalNavigationComponentProps } from "@sal/onevent-portal";
import { useNavigate } from "react-router-dom";
import { getTerm } from "../locale/index";
import { useAuth } from "@sal/authentication.react";
import { useQuery } from "@tanstack/react-query";
import { OnEventApiResponse } from "@origin/platform-types";
import { useMemo } from "react";
import { BulkUpdatePageConfiguration } from "../models/Configuration/BulkUpdatePageConfiguration";

export function VerticalNavigationActionButton({ collapsed }: VerticalNavigationComponentProps) {
	const {
		organisations: { getOrganisations }
	} = usePortalApplicationContext();
	const organisations = getOrganisations().map((x) => x.id); 
	const theme = useTheme();
	const navigate = useNavigate();

	const auth = useAuth();
	const canCreateNewOrder = auth.userHasPermission("apI_PortalOrder", "Write");
	const canCreateNewBooking = auth.userHasPermission("apI_PortalShipment", "Write");

	const bulkUpdatePageConfigurationQuery = useQuery<OnEventApiResponse<BulkUpdatePageConfiguration>, Error>(
		["/ObjectConfiguration", organisations[0]],
		() =>
			httpService.get(
				`ObjectConfiguration?objectID=${organisations[0]}&objectType=Contact&objectConfigurationType=BulkUpdatePage`
			),
		{
			enabled: !!organisations[0]
		}
	);

	const canBulkUpdate = useMemo(() => {
		return canCreateNewOrder && !!bulkUpdatePageConfigurationQuery?.data?.data?.pageComponent;
	}, [canCreateNewOrder, bulkUpdatePageConfigurationQuery?.data?.data?.pageComponent]);

	return (
		<>
			<Box alignItems="center" justifyContent="center" width="100%" paddingX={collapsed ? 0 : 6} paddingTop={collapsed ? 4 : 12}>
				<Box
					display="flex"
					alignItems="center"
					justifyContent="center"
					width="100%"
					paddingX={collapsed ? 0 : 6}
				>
					{canCreateNewOrder && (
						<Button
							variant="contained"
							color="primary"
							fullWidth={!collapsed}
							title={getTerm("navigation_NewOrder")}
							sx={{
								paddingY: collapsed ? theme.spacing(2) : theme.spacing(3),
								minWidth: collapsed ? "auto" : "initial"
							}}
							onClick={() => navigate({ pathname: "/ImportOrder" })}
						>
							<Typography
								fontSize={collapsed ? "large" : "0.85rem"}
								color={theme.palette.common.white}
								sx={{ display: "flex", alignItems: "center", gap: 1 }}
							>
								<AddIcon fontSize="inherit" />
								{!collapsed && <span>{getTerm("navigation_NewOrder")}</span>}
							</Typography>
						</Button>
					)}
				</Box>

				{canCreateNewBooking && (
					<Box
						display="flex"
						alignItems="center"
						justifyContent="center"
						width="100%"
						paddingX={collapsed ? 0 : 6}
						paddingTop={2}
					>
						<Button
							variant="contained"
							color="primary"
							fullWidth={!collapsed}
							title={getTerm("navigation_NewBooking")}
							sx={{
								paddingY: collapsed ? theme.spacing(2) : theme.spacing(3),
								minWidth: collapsed ? "auto" : "initial"
							}}
							onClick={() => navigate({ pathname: "/ExportBooking" })}
						>
							<Typography
								fontSize={collapsed ? "large" : "0.85rem"}
								color={theme.palette.common.white}
								sx={{ display: "flex", alignItems: "center", gap: 1 }}
							>
								<AddIcon fontSize="inherit" />
								{!collapsed && <span>{getTerm("navigation_NewBooking")}</span>}
							</Typography>
						</Button>
					</Box>
				)}
				{canBulkUpdate && (
					<Box
						display="flex"
						alignItems="center"
						justifyContent="center"
						width="100%"
						paddingX={collapsed ? 0 : 6}
						paddingTop={2}
					>
						<Button
							variant="contained"
							color="primary"
							fullWidth={!collapsed}
							title={getTerm("navigation_BulkUpdate")}
							sx={{
								paddingY: collapsed ? theme.spacing(2) : theme.spacing(3),
								minWidth: collapsed ? "auto" : "initial"
							}}
							onClick={() => navigate({ pathname: "/BulkUpdate" })}
						>
							<Typography
								fontSize={collapsed ? "large" : "0.85rem"}
								color={theme.palette.common.white}
								sx={{ display: "flex", alignItems: "center", gap: 1 }}
							>
								<UpgradeIcon fontSize={collapsed ? "inherit" : "small"} />
								{!collapsed && <span>{getTerm("navigation_BulkUpdate")}</span>}
							</Typography>
						</Button>
					</Box>
				)}
			</Box>
		</>
	);
}
